import React, { useEffect, useState } from "react";
import {
    Box, Paper, Typography, TextField, Button, Stack, Divider, CircularProgress, Alert,
} from "@mui/material";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import { api } from "../../util/api";
import { notify } from "../../util/toast";
import { useMyPermissions } from "../../util/useMyPermissions";

/**
 * 관리자 전용 시스템 설정 페이지.
 *
 * <p>현재 항목: 로그인 세션 유지 시간(분). 저장은 백엔드에서 GLOBAL_ADMIN 권한을 강제하며,
 * 여기서도 관리자가 아니면 편집 UI를 감춘다. 변경값은 이후 새 로그인/세션 연장부터 적용된다.
 */
export default function SystemSettingPage() {
    const { isAdmin, loading: permLoading } = useMyPermissions();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [minutes, setMinutes] = useState("");
    const [range, setRange] = useState({ min: 5, max: 1440 });

    const [bulk, setBulk] = useState({ maxRecipients: 20, rateLimitWindowMinutes: 10, maxBatchesPerWindow: 1 });
    const [bulkSaving, setBulkSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/api/admin/system-setting/session")
            .then((data) => {
                if (!alive) return;
                setMinutes(String(data?.sessionMinutes ?? 60));
                setRange({ min: data?.minMinutes ?? 5, max: data?.maxMinutes ?? 1440 });
            })
            .catch(() => { if (alive) notify("설정을 불러오지 못했습니다.", "error"); })
            .finally(() => { if (alive) setLoading(false); });
        api.get("/api/admin/system-setting/bulk-policy")
            .then((d) => {
                if (!alive) return;
                setBulk({
                    maxRecipients: d?.maxRecipients ?? 20,
                    rateLimitWindowMinutes: d?.rateLimitWindowMinutes ?? 10,
                    maxBatchesPerWindow: d?.maxBatchesPerWindow ?? 1,
                });
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const handleSaveBulk = async () => {
        const max = parseInt(bulk.maxRecipients, 10);
        const win = parseInt(bulk.rateLimitWindowMinutes, 10);
        const per = parseInt(bulk.maxBatchesPerWindow, 10);
        if ([max, win, per].some(Number.isNaN) || max < 1 || win < 0 || per < 1) {
            notify("값을 확인해주세요.", "warning");
            return;
        }
        setBulkSaving(true);
        try {
            const d = await api.put("/api/admin/system-setting/bulk-policy", {
                maxRecipients: max, rateLimitWindowMinutes: win, maxBatchesPerWindow: per,
            });
            setBulk({
                maxRecipients: d?.maxRecipients ?? max,
                rateLimitWindowMinutes: d?.rateLimitWindowMinutes ?? win,
                maxBatchesPerWindow: d?.maxBatchesPerWindow ?? per,
            });
            notify("단체 메일 정책이 저장되었습니다.", "success");
        } catch (e) {
            notify(e?.message || "저장에 실패했습니다.", "error");
        } finally {
            setBulkSaving(false);
        }
    };

    const handleSave = async () => {
        const n = parseInt(minutes, 10);
        if (Number.isNaN(n) || n < range.min || n > range.max) {
            notify(`세션 유지 시간은 ${range.min}~${range.max}분 사이여야 합니다.`, "warning");
            return;
        }
        setSaving(true);
        try {
            const data = await api.put("/api/admin/system-setting/session", { sessionMinutes: n });
            setMinutes(String(data?.sessionMinutes ?? n));
            notify("세션 유지 시간이 저장되었습니다. 새 로그인·연장부터 적용됩니다.", "success");
        } catch (e) {
            notify(e?.message || "저장에 실패했습니다.", "error");
        } finally {
            setSaving(false);
        }
    };

    if (permLoading || loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 640, mx: "auto", pt: 4, pb: 8 }}>
            <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2.5 }}>
                <SettingsRoundedIcon sx={{ color: "#334155" }} />
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>시스템 설정</Typography>
            </Stack>

            <Paper elevation={0} sx={{ border: "1px solid rgba(15,23,42,0.08)", borderRadius: 2, p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <AccessTimeRoundedIcon sx={{ fontSize: 20, color: "#2563eb" }} />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>로그인 세션 유지 시간</Typography>
                </Stack>
                <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
                    로그인 후 자동 로그아웃까지의 시간(분)입니다. 직원 화면 좌측 하단 타이머에 표시되며,
                    변경값은 이후 <b>새 로그인 및 세션 연장</b>부터 적용됩니다. (허용 범위 {range.min}~{range.max}분)
                </Typography>

                {!isAdmin && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        설정 변경은 전체 관리자(GLOBAL_ADMIN)만 가능합니다. 현재 값은 조회만 가능합니다.
                    </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" spacing={1.5} alignItems="center">
                    <TextField
                        type="number"
                        size="small"
                        label="세션 유지 시간"
                        value={minutes}
                        disabled={!isAdmin || saving}
                        onChange={(e) => setMinutes(e.target.value)}
                        inputProps={{ min: range.min, max: range.max, step: 5 }}
                        sx={{ width: 200 }}
                        InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: "#64748b", ml: 1 }}>분</Typography> }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!isAdmin || saving}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1 }}
                    >
                        {saving ? "저장 중..." : "저장"}
                    </Button>
                </Stack>
                <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1.5 }}>
                    예) 60 = 1시간, 30 = 30분. 기본값은 60분(1시간)입니다.
                </Typography>
            </Paper>

            {/* 단체 메일 정책 */}
            <Paper elevation={0} sx={{ border: "1px solid rgba(15,23,42,0.08)", borderRadius: 2, p: 3, mt: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <CampaignRoundedIcon sx={{ fontSize: 20, color: "#2563eb" }} />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>단체 메일 정책</Typography>
                </Stack>
                <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
                    단체(대량) 메일의 <b>배치당 최대 수신자 수</b>와 <b>발송 빈도(rate limit)</b>를 제한합니다.
                    기본값은 20명 · 10분에 1회입니다.
                </Typography>

                {!isAdmin && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        설정 변경은 전체 관리자(GLOBAL_ADMIN)만 가능합니다.
                    </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField
                        type="number" size="small" label="최대 수신자"
                        value={bulk.maxRecipients} disabled={!isAdmin || bulkSaving}
                        onChange={(e) => setBulk(b => ({ ...b, maxRecipients: e.target.value }))}
                        inputProps={{ min: 1, max: 500 }} sx={{ width: 150 }}
                        InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: "#64748b", ml: 1 }}>명</Typography> }}
                    />
                    <TextField
                        type="number" size="small" label="제한 시간창"
                        value={bulk.rateLimitWindowMinutes} disabled={!isAdmin || bulkSaving}
                        onChange={(e) => setBulk(b => ({ ...b, rateLimitWindowMinutes: e.target.value }))}
                        inputProps={{ min: 0, max: 1440 }} sx={{ width: 150 }}
                        InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: "#64748b", ml: 1 }}>분</Typography> }}
                    />
                    <TextField
                        type="number" size="small" label="시간창당 횟수"
                        value={bulk.maxBatchesPerWindow} disabled={!isAdmin || bulkSaving}
                        onChange={(e) => setBulk(b => ({ ...b, maxBatchesPerWindow: e.target.value }))}
                        inputProps={{ min: 1, max: 1000 }} sx={{ width: 150 }}
                        InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: "#64748b", ml: 1 }}>회</Typography> }}
                    />
                    <Button
                        variant="contained" onClick={handleSaveBulk} disabled={!isAdmin || bulkSaving}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1 }}
                    >
                        {bulkSaving ? "저장 중..." : "저장"}
                    </Button>
                </Stack>
                <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1.5 }}>
                    예) 최대 20명 · 10분 · 1회 = "한 번에 20명까지, 10분에 1회만 발송".
                </Typography>
            </Paper>
        </Box>
    );
}
